import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import {
  SwapLegsContainer, SwapLegPanel, SwapLegHeader, SwapLegTitle, SwapLegIcon,
  SwapArrowDivider, SwapArrow, LegFormRow, LegFormRowSingle, SwapDirectionToggle, DirectionOption
} from './shared/SwapLegStyledComponents';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars, SettlementQuery } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import axios from 'axios';
import styled from 'styled-components';

import { DateField } from '../shared/DateField';

// ADR 0009 §4.2: a swaption's dates run on its currency's calendar, not the book's. The ticket
// shows the settle date the server will store; the browser's count stands in until the server
// answers.
const settleQuery = (tradeDate: string, currency?: string): SettlementQuery =>
  ({ tradeDate, ticketType: 'SWAPTION', convention: 'T+1', currency: currency || 'USD' });

/** Applies the server's settle date if the form still holds the inputs and the date it was asked about. */
const applyServerSettleDate = (setFormData: any, query: SettlementQuery, expected: string) => {
  resolveSettleDate(query).then(settleDate => setFormData((prev: any) =>
    prev.tradeDate === query.tradeDate && (prev.currency || 'USD') === query.currency
      && prev.settleDate === expected && settleDate && settleDate !== expected
      ? { ...prev, settleDate } : prev));
};

/** After a trade date or currency change: the browser's count at once, then the server's. */
const refreshSettleDate = (setFormData: any, query: SettlementQuery) => {
  if (!query.tradeDate) return;
  const provisional = provisionalSettleDate(query);
  setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
  applyServerSettleDate(setFormData, query, provisional);
};

// ── Swaption-specific styled components ──

const OptionHeaderPanel = styled.div`
  background: rgba(245, 158, 11, 0.06);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.25rem;
`;

const OptionHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(245, 158, 11, 0.15);
`;

const OptionHeaderLabel = styled.h3`
  font-size: 0.9375rem;
  font-weight: 700;
  color: #fbbf24;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const OptionHeaderIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  font-size: 14px;
  background: rgba(245, 158, 11, 0.15);
`;

const PremiumCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #d1d5db;
  cursor: pointer;
  padding: 0.75rem 0 0 0;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #f59e0b;
    cursor: pointer;
  }
`;

const DerivativesSwaptionTicketScreen: React.FC = () => {
  const location = useLocation();

  const getPendingTradeData = () => {
    try {
      const pendingData = sessionStorage.getItem('pendingTradeData');
      if (pendingData) {
        const parsed = JSON.parse(pendingData);
        sessionStorage.removeItem('pendingTradeData');
        return parsed;
      }
    } catch (error) {
      console.error('Error reading pending trade data:', error);
    }
    return null;
  };

  const pendingTrade = getPendingTradeData();

  const getInitialFormData = () => {
    const hedgeData = location.state?.hedgeData;
    const portfolioId = location.state?.portfolioId;
    const tradeDate = pendingTrade?.tradeDate || getTodayDate(calendarsFor('SWAPTION', pendingTrade?.currency));

    const baseFormData = {
      // Swaption direction: Long Receiver = buy option to receive fixed
      // Long Payer = buy option to pay fixed
      direction: (pendingTrade?.direction || 'LONG_RECEIVER') as string,
      assetManager: pendingTrade?.assetManager || '',
      portfolioManager: pendingTrade?.portfolioManager || '',
      assetClass: 'Derivatives',
      assetSubClass: 'Swaptions',
      security: pendingTrade?.securityName || pendingTrade?.security || '',
      identifier: pendingTrade?.cusip || pendingTrade?.isin || pendingTrade?.identifier || '',
      // Common
      tradeDate,
      settleDate: pendingTrade?.settleDate || provisionalSettleDate(settleQuery(tradeDate, pendingTrade?.currency)),
      portfolio: pendingTrade?.portfolioName || pendingTrade?.portfolio || '',
      submittedBy: pendingTrade?.submittedBy || getCurrentOperatorName(),
      executedBy: pendingTrade?.executedBy || '',
      currency: pendingTrade?.currency || 'USD',
      notes: pendingTrade?.notes || '',
      // Option fields
      exerciseType: pendingTrade?.exerciseType || 'EUROPEAN',
      expirationDate: pendingTrade?.expirationDate || '',
      strike: pendingTrade?.strike?.toString() || '',
      premium: pendingTrade?.premium?.toString() || '',
      premiumCurrency: pendingTrade?.premiumCurrency || 'USD',
      premiumPaidAtExpiry: pendingTrade?.premiumPaidAtExpiry || false,
      fee: pendingTrade?.fee?.toString() || '',
      feeDate: pendingTrade?.feeDate || '',
      delta: pendingTrade?.delta?.toString() || '',
      // Underlying swap fields
      swapStartDate: pendingTrade?.swapStartDate || '',
      swapEndDate: pendingTrade?.swapEndDate || '',
      swapTenor: pendingTrade?.swapTenor || '5Y',
      notionalAmount: pendingTrade?.notionalAmount?.toString() || '',
      // Fixed leg
      fixedCurrency: pendingTrade?.fixedCurrency || 'USD',
      fixedRate: pendingTrade?.fixedRate?.toString() || '',
      fixedDayCount: pendingTrade?.fixedDayCount || '30/360',
      fixedPayFrequency: pendingTrade?.fixedPayFrequency || 'Semi-Annual',
      // Float leg
      floatCurrency: pendingTrade?.floatCurrency || 'USD',
      floatIndex: pendingTrade?.floatIndex || 'SOFR',
      floatSpread: pendingTrade?.floatSpread?.toString() || '',
      floatDayCount: pendingTrade?.floatDayCount || 'ACT/360',
      floatPayFrequency: pendingTrade?.floatPayFrequency || 'Quarterly',
      floatResetFrequency: pendingTrade?.floatResetFrequency || 'Quarterly',
      floatLeverage: pendingTrade?.floatLeverage?.toString() || '1',
    };

    if (hedgeData) {
      if (hedgeData.securityName) baseFormData.security = hedgeData.securityName;
      if (hedgeData.identifier) baseFormData.identifier = hedgeData.identifier;
      if (hedgeData.notionalAmount) {
        const notional = typeof hedgeData.notionalAmount === 'number'
          ? hedgeData.notionalAmount : parseFloat(hedgeData.notionalAmount);
        if (!isNaN(notional)) {
          baseFormData.notionalAmount = notional.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
      }
      if (portfolioId) baseFormData.portfolio = String(portfolioId);
    }

    return baseFormData;
  };

  const initialFormData = getInitialFormData();

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    if (!formData.assetManager?.trim()) errors.assetManager = 'Asset Manager is required';
    if (!formData.portfolioManager?.trim()) errors.portfolioManager = 'Portfolio Manager is required';
    if (!formData.notionalAmount?.trim()) errors.notionalAmount = 'Notional Amount is required';
    if (!formData.strike?.trim()) errors.strike = 'Strike is required';
    if (!formData.premium?.trim()) errors.premium = 'Premium is required (the option\'s real inception cash)';
    if (!formData.expirationDate) errors.expirationDate = 'Expiration Date is required';
    if (!formData.swapTenor?.trim() && !formData.swapEndDate) errors.swapTenor = 'Swap Tenor or End Date is required';
    if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
    if (!formData.tradeDate) errors.tradeDate = 'Trade Date is required';
    if (!formData.settleDate) errors.settleDate = 'Settle Date is required';
    if (action === 'execute') {
      if (!formData.executedBy?.trim()) errors.executedBy = 'Executed By is required for execution';
    }
    return errors;
  };

  // Swaption E2E rewire (2026-08-07, final lane): the ticket's terms ARE the
  // bespoke contract — /prepare mints dim_swaption_contract + a classified
  // per-contract SMF (SW-prefix 9-char CUSIP) and returns the sourceRef +
  // signed inception cash (OPTIONS-family: premium is REAL cash — long pays,
  // short receives) the ticket declares as netAmount.
  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);

    const notional = parseAmountShorthand(formData.notionalAmount);
    const isLong = formData.direction.startsWith('LONG');
    const tenorYears = formData.swapTenor ? parseFloat(String(formData.swapTenor).replace(/[^0-9.]/g, '')) : null;

    const prepareRes = await axios.post('/api/derivatives/swaption/prepare', {
      portfolioId: portfolio.id,
      direction: formData.direction,
      exerciseStyle: formData.exerciseType || 'EUROPEAN',
      notional,
      currency: formData.currency || 'USD',
      strikeRate: formData.strike ? parseFloat(formData.strike) : null,
      premiumAmount: formData.premium ? parseAmountShorthand(formData.premium) : null,
      delta: formData.delta ? parseFloat(formData.delta) : null,
      tradeDate: formData.tradeDate,
      expirationDate: formData.expirationDate || null,
      settlementDate: formData.settleDate || null,
      underlyingTenorYears: tenorYears,
      underlyingIndex: formData.floatIndex || null,
      underlyingFrequency: formData.floatPayFrequency || null,
      underlyingDayCount: formData.floatDayCount || null,
    });
    const { securityId, sourceRef, inceptionCash } = prepareRes.data;

    return {
      transactionType: isLong ? 'BUY' : 'SELL',
      securityId,
      portfolioId: portfolio.id,
      quantity: notional,
      price: 100,
      amount: notional,
      netAmount: inceptionCash ?? 0,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      notes: formData.notes || `Swaption ${formData.direction} strike ${formData.strike}% exp ${formData.expirationDate}`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'SWAPTION',
      eventType: 'ACQUIRE',
      sourceRef,
    };
  };

  const loadTransactionData = async (transaction: any) => {
    const isLong = transaction.transactionType === 'BUY';
    // Infer receiver/payer from context — default to receiver for long
    const direction = isLong ? 'LONG_RECEIVER' : 'SHORT_RECEIVER';
    const tradeDate = transaction.tradeDate || getTodayDate(calendarsFor('SWAPTION', transaction.currency));

    const base = {
      direction,
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      assetClass: 'Derivatives',
      assetSubClass: 'Swaptions',
      security: transaction.securityName || transaction.security || '',
      identifier: transaction.cusip || transaction.isin || transaction.identifier || '',
      tradeDate,
      settleDate: transaction.settleDate || provisionalSettleDate(settleQuery(tradeDate, transaction.currency)),
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      executedBy: transaction.executedBy || '',
      currency: transaction.currency || 'USD',
      notes: transaction.notes || '',
      exerciseType: 'EUROPEAN',
      expirationDate: transaction.expiryDate || '',
      strike: transaction.strikePrice?.toString() || '',
      premium: '',
      premiumCurrency: 'USD',
      premiumPaidAtExpiry: false,
      fee: '',
      feeDate: '',
      delta: transaction.delta?.toString() || '',
      swapStartDate: '',
      swapEndDate: '',
      swapTenor: transaction.tenor || '5Y',
      notionalAmount: transaction.notionalAmount?.toString() || transaction.amount?.toString() || '',
      fixedCurrency: transaction.currency || 'USD',
      fixedRate: transaction.fixedRate?.toString() || '',
      fixedDayCount: '30/360',
      fixedPayFrequency: transaction.paymentFrequency || 'Semi-Annual',
      floatCurrency: transaction.currency || 'USD',
      floatIndex: transaction.floatingRateIndex || 'SOFR',
      floatSpread: transaction.floatingRateSpread?.toString() || '',
      floatDayCount: 'ACT/360',
      floatPayFrequency: 'Quarterly',
      floatResetFrequency: 'Quarterly',
      floatLeverage: '1',
    };
    const m = (transaction.sourceRef || '').match(/swaption-contract:(\d+)/);
    if (!m) return base;
    try {
      const c = (await axios.get(`/api/derivatives/swaption/contract/${m[1]}`)).data;
      return {
        ...base,
        direction: c.optionDirection || base.direction,
        exerciseType: c.exerciseStyle || base.exerciseType,
        expirationDate: c.expirationDate || base.expirationDate,
        strike: c.strikeRate != null ? String(parseFloat(c.strikeRate)) : base.strike,
        premium: c.premiumAmount != null ? String(parseFloat(c.premiumAmount)) : '',
        delta: c.delta != null ? String(parseFloat(c.delta)) : base.delta,
        notionalAmount: c.notionalAmount != null ? String(c.notionalAmount) : base.notionalAmount,
        swapTenor: c.underlyingTenorYears != null ? `${parseFloat(c.underlyingTenorYears)}Y` : base.swapTenor,
        floatIndex: c.underlyingIndex || base.floatIndex,
        floatPayFrequency: c.underlyingFrequency || base.floatPayFrequency,
        floatDayCount: c.underlyingDayCount || base.floatDayCount,
      };
    } catch { return base; }
  };

  return (
    <BaseTicketScreen
      title="Swaption Trade Entry"
      assetClass="Derivatives"
      assetSubClass="Swaptions"
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur, editingTransactionId }) => (
        <SwaptionFormContent
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          dimensionData={dimensionData}
          transactionStatus={transactionStatus}
          isFieldDisabled={isFieldDisabled}
          handleInputChange={handleInputChange}
          handleInputBlur={handleInputBlur}
          location={location}
          editingTransactionId={editingTransactionId}
        />
      )}
    </BaseTicketScreen>
  );
};

const SwaptionFormContent: React.FC<{
  formData: any;
  setFormData: any;
  errors: any;
  dimensionData: any;
  transactionStatus: any;
  isFieldDisabled: any;
  handleInputChange: any;
  handleInputBlur: any;
  location: any;
  editingTransactionId: number | null;
}> = ({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur, location, editingTransactionId }) => {

  useEffect(() => {
    const portfolioId = location.state?.portfolioId;
    const portfolioName = location.state?.portfolioName;
    const currentPortfolio = formData.portfolio;
    const isPortfolioId = currentPortfolio && /^\d+$/.test(String(currentPortfolio).trim());
    if (portfolioName && (!currentPortfolio || isPortfolioId)) {
      setFormData((prev: any) => ({ ...prev, portfolio: portfolioName }));
    } else if (portfolioId && dimensionData.portfolios.length > 0 && (!currentPortfolio || isPortfolioId)) {
      const portfolio = dimensionData.portfolios.find((p: any) => String(p.id) === String(portfolioId));
      if (portfolio?.portfolioName) {
        setFormData((prev: any) => ({ ...prev, portfolio: portfolio.portfolioName }));
      }
    }
  }, [dimensionData.portfolios, location.state?.portfolioId, location.state?.portfolioName, formData.portfolio, setFormData]);

  // A new ticket asks the server for its settle date on open; a reopened trade keeps its own.
  useEffect(() => {
    if (editingTransactionId || location.state?.transactionId || location.state?.id || !formData.tradeDate) return;
    applyServerSettleDate(setFormData, settleQuery(formData.tradeDate, formData.currency), formData.settleDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isReceiver = formData.direction === 'LONG_RECEIVER' || formData.direction === 'SHORT_RECEIVER';

  return (
    <>
      {/* ── Row 1: Direction, Trade Date, Settle Date ── */}
      <FormRow>
        <FormGroup>
          <Label>Direction*</Label>
          <SwapDirectionToggle>
            <DirectionOption
              variant="receive"
              active={formData.direction === 'LONG_RECEIVER'}
              disabled={isFieldDisabled('direction')}
              onClick={() => handleInputChange('direction', 'LONG_RECEIVER')}
            >
              Long Receiver
            </DirectionOption>
            <DirectionOption
              variant="pay"
              active={formData.direction === 'LONG_PAYER'}
              disabled={isFieldDisabled('direction')}
              onClick={() => handleInputChange('direction', 'LONG_PAYER')}
            >
              Long Payer
            </DirectionOption>
            <DirectionOption
              variant="pay"
              active={formData.direction === 'SHORT_RECEIVER'}
              disabled={isFieldDisabled('direction')}
              onClick={() => handleInputChange('direction', 'SHORT_RECEIVER')}
            >
              Short Receiver
            </DirectionOption>
            <DirectionOption
              variant="receive"
              active={formData.direction === 'SHORT_PAYER'}
              disabled={isFieldDisabled('direction')}
              onClick={() => handleInputChange('direction', 'SHORT_PAYER')}
            >
              Short Payer
            </DirectionOption>
          </SwapDirectionToggle>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="tradeDate">Trade Date*</Label>
          <DateField
            id="tradeDate"
            value={formData.tradeDate ?? ''}
            className={errors.tradeDate ? 'error' : ''}
            disabled={isFieldDisabled('tradeDate')}
            calendars={calendarsFor('SWAPTION', formData.currency)}
            onChange={iso => {
              handleInputChange('tradeDate', iso);
              refreshSettleDate(setFormData, settleQuery(iso, formData.currency));
            }}
          />
          {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="settleDate">Settle Date*</Label>
          <DateField
            id="settleDate"
            value={formData.settleDate ?? ''}
            className={errors.settleDate ? 'error' : ''}
            disabled={isFieldDisabled('settleDate')}
            calendars={settlementCalendars({ ticketType: 'SWAPTION', currency: formData.currency })}
            onChange={iso => handleInputChange('settleDate', iso)}
          />
          {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* ── Row 2: Asset Manager, PM, Executed By ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="assetManager">Asset Manager*</Label>
          <Select
            id="assetManager"
            value={formData.assetManager}
            onChange={(e) => handleInputChange('assetManager', e.target.value)}
            className={errors.assetManager ? 'error' : ''}
            disabled={isFieldDisabled('assetManager')}
          >
            <option value="">Select Asset Manager</option>
            {dimensionData.assetManagers.map((am: any) => (
              <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>
            ))}
          </Select>
          {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="portfolioManager">Portfolio Manager*</Label>
          <Select
            id="portfolioManager"
            value={formData.portfolioManager}
            onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
            className={errors.portfolioManager ? 'error' : ''}
            disabled={isFieldDisabled('portfolioManager')}
          >
            <option value="">Select Portfolio Manager</option>
            {dimensionData.portfolioManagers.map((pm: any) => (
              <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>
            ))}
          </Select>
          {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
          <Select
            id="executedBy"
            value={formData.executedBy}
            onChange={(e) => handleInputChange('executedBy', e.target.value)}
            disabled={isFieldDisabled('executedBy')}
            className={errors.executedBy ? 'error' : ''}
          >
            <option value="">Select</option>
            {dimensionData.users.map((user: any) => (
              <option key={user.id} value={user.fullName}>{user.fullName} {user.role ? `(${user.role})` : ''}</option>
            ))}
          </Select>
          {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* ── Row 3: Contract Name, Identifier ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="security">Contract Name</Label>
          <Input
            id="security"
            type="text"
            value={formData.security}
            onChange={(e) => handleInputChange('security', e.target.value)}
            placeholder="e.g., 1Y into 5Y Receiver Swaption"
            disabled={isFieldDisabled('security')}
          />
        </FormGroup>

        {/* Identifier field REMOVED (Swaption E2E 2026-08-07): the contract +
            classified SMF mint from the ticket's terms at /prepare. */}

        <FormGroup>
          <Label htmlFor="currency">Currency*</Label>
          <Select
            id="currency"
            value={formData.currency}
            onChange={(e) => {
              handleInputChange('currency', e.target.value);
              refreshSettleDate(setFormData, settleQuery(formData.tradeDate, e.target.value));
            }}
            disabled={isFieldDisabled('currency')}
          >
            {dimensionData.currencies.map((c: any) => (
              <option key={c.id} value={c.currencyCode}>{c.currencyCode}</option>
            ))}
          </Select>
        </FormGroup>
      </FormRow>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/*  OPTION TERMS PANEL                                                */}
      {/* ════════════════════════════════════════════════════════════════════ */}

      <OptionHeaderPanel>
        <OptionHeaderTitle>
          <OptionHeaderIcon>&#x1D4AA;</OptionHeaderIcon>
          <OptionHeaderLabel>Option Terms</OptionHeaderLabel>
        </OptionHeaderTitle>

        <FormRow>
          <FormGroup>
            <Label htmlFor="notionalAmount">Notional Amount*</Label>
            <Input
              id="notionalAmount"
              type="text"
              value={formData.notionalAmount}
              onChange={(e) => handleInputChange('notionalAmount', e.target.value)}
              onBlur={(e) => {
                const parsed = parseAmountShorthand(e.target.value);
                if (parsed > 0) {
                  setFormData((prev: any) => ({ ...prev, notionalAmount: formatAmountWithCommas(parsed) }));
                }
              }}
              placeholder="e.g., 50MM"
              className={errors.notionalAmount ? 'error' : ''}
              disabled={isFieldDisabled('notionalAmount')}
            />
            {errors.notionalAmount && <FieldError>{errors.notionalAmount}</FieldError>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="exerciseType">Exercise Type</Label>
            <Select
              id="exerciseType"
              value={formData.exerciseType}
              onChange={(e) => handleInputChange('exerciseType', e.target.value)}
              disabled={isFieldDisabled('exerciseType')}
            >
              <option value="EUROPEAN">European</option>
              <option value="BERMUDAN">Bermudan</option>
              <option value="AMERICAN">American</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="expirationDate">Expiration Date*</Label>
            <DateField
              id="expirationDate"
              value={formData.expirationDate ?? ''}
              className={errors.expirationDate ? 'error' : ''}
              disabled={isFieldDisabled('expirationDate')}
              onChange={iso => handleInputChange('expirationDate', iso)}
            />
            {errors.expirationDate && <FieldError>{errors.expirationDate}</FieldError>}
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup>
            <Label htmlFor="strike">Strike (%)*</Label>
            <Input
              id="strike"
              type="number"
              step="0.0001"
              value={formData.strike}
              onChange={(e) => handleInputChange('strike', e.target.value)}
              placeholder="e.g., 4.2500"
              className={errors.strike ? 'error' : ''}
              disabled={isFieldDisabled('strike')}
            />
            {errors.strike && <FieldError>{errors.strike}</FieldError>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="premium">Premium</Label>
            <Input
              id="premium"
              type="number"
              step="0.01"
              value={formData.premium}
              onChange={(e) => handleInputChange('premium', e.target.value)}
              placeholder="e.g., 125000.00"
              disabled={isFieldDisabled('premium')}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="delta">Delta</Label>
            <Input
              id="delta"
              type="number"
              step="0.01"
              value={formData.delta}
              onChange={(e) => handleInputChange('delta', e.target.value)}
              placeholder="e.g., 0.45"
              disabled={isFieldDisabled('delta')}
            />
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup>
            <Label htmlFor="fee">Fee</Label>
            <Input
              id="fee"
              type="number"
              step="0.01"
              value={formData.fee}
              onChange={(e) => handleInputChange('fee', e.target.value)}
              placeholder="e.g., 5000.00"
              disabled={isFieldDisabled('fee')}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="feeDate">Fee Date</Label>
            <DateField
              id="feeDate"
              value={formData.feeDate ?? ''}
              disabled={isFieldDisabled('feeDate')}
              onChange={iso => handleInputChange('feeDate', iso)}
            />
          </FormGroup>

          <FormGroup>
            <PremiumCheckbox>
              <input
                type="checkbox"
                checked={formData.premiumPaidAtExpiry}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, premiumPaidAtExpiry: e.target.checked }))}
                disabled={isFieldDisabled('premiumPaidAtExpiry')}
              />
              Premium Paid At Expiry
            </PremiumCheckbox>
          </FormGroup>
        </FormRow>
      </OptionHeaderPanel>

      {/* ── Underlying Swap Dates ── */}
      <FormRow>
        <FormGroup>
          <Label htmlFor="swapStartDate">Swap Start Date</Label>
          <DateField
            id="swapStartDate"
            value={formData.swapStartDate ?? ''}
            disabled={isFieldDisabled('swapStartDate')}
            onChange={iso => handleInputChange('swapStartDate', iso)}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="swapTenor">Swap Tenor*</Label>
          <Input
            id="swapTenor"
            type="text"
            value={formData.swapTenor}
            onChange={(e) => handleInputChange('swapTenor', e.target.value)}
            placeholder="e.g., 5Y, 10Y"
            className={errors.swapTenor ? 'error' : ''}
            disabled={isFieldDisabled('swapTenor')}
          />
          {errors.swapTenor && <FieldError>{errors.swapTenor}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="swapEndDate">Swap End Date</Label>
          <DateField
            id="swapEndDate"
            value={formData.swapEndDate ?? ''}
            disabled={isFieldDisabled('swapEndDate')}
            onChange={iso => handleInputChange('swapEndDate', iso)}
          />
        </FormGroup>
      </FormRow>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/*  TWO-PANEL UNDERLYING SWAP LEGS                                    */}
      {/* ════════════════════════════════════════════════════════════════════ */}

      <SwapLegsContainer>
        {/* ── FIXED LEG ── */}
        <SwapLegPanel variant={isReceiver ? 'receive' : 'pay'}>
          <SwapLegHeader variant={isReceiver ? 'receive' : 'pay'}>
            <SwapLegIcon variant={isReceiver ? 'receive' : 'pay'}>
              {isReceiver ? '↓' : '↑'}
            </SwapLegIcon>
            <SwapLegTitle variant={isReceiver ? 'receive' : 'pay'}>
              {isReceiver ? 'Receive' : 'Pay'} Fixed
            </SwapLegTitle>
          </SwapLegHeader>

          <LegFormRowSingle>
            <FormGroup>
              <Label htmlFor="fixedRate">Coupon / Fixed Rate (%)</Label>
              <Input
                id="fixedRate"
                type="number"
                step="0.0001"
                value={formData.fixedRate}
                onChange={(e) => handleInputChange('fixedRate', e.target.value)}
                placeholder="e.g., 4.2500"
                disabled={isFieldDisabled('fixedRate')}
              />
            </FormGroup>
          </LegFormRowSingle>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="fixedPayFrequency">Pay Frequency</Label>
              <Select
                id="fixedPayFrequency"
                value={formData.fixedPayFrequency}
                onChange={(e) => handleInputChange('fixedPayFrequency', e.target.value)}
                disabled={isFieldDisabled('fixedPayFrequency')}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
                <option value="Annual">Annual</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="fixedDayCount">Day Count</Label>
              <Select
                id="fixedDayCount"
                value={formData.fixedDayCount}
                onChange={(e) => handleInputChange('fixedDayCount', e.target.value)}
                disabled={isFieldDisabled('fixedDayCount')}
              >
                <option value="30/360">30/360</option>
                <option value="ACT/360">ACT/360</option>
                <option value="ACT/365">ACT/365</option>
                <option value="ACT/ACT">ACT/ACT</option>
              </Select>
            </FormGroup>
          </LegFormRow>
        </SwapLegPanel>

        {/* ── ARROW DIVIDER ── */}
        <SwapArrowDivider>
          <SwapArrow>⇄</SwapArrow>
        </SwapArrowDivider>

        {/* ── FLOAT LEG ── */}
        <SwapLegPanel variant={isReceiver ? 'pay' : 'receive'}>
          <SwapLegHeader variant={isReceiver ? 'pay' : 'receive'}>
            <SwapLegIcon variant={isReceiver ? 'pay' : 'receive'}>
              {isReceiver ? '↑' : '↓'}
            </SwapLegIcon>
            <SwapLegTitle variant={isReceiver ? 'pay' : 'receive'}>
              {isReceiver ? 'Pay' : 'Receive'} Float
            </SwapLegTitle>
          </SwapLegHeader>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="floatIndex">Index</Label>
              <Select
                id="floatIndex"
                value={formData.floatIndex}
                onChange={(e) => handleInputChange('floatIndex', e.target.value)}
                disabled={isFieldDisabled('floatIndex')}
              >
                <option value="SOFR">SOFR</option>
                <option value="EURIBOR">EURIBOR</option>
                <option value="SONIA">SONIA</option>
                <option value="TONAR">TONAR</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="floatSpread">Spread (bps)</Label>
              <Input
                id="floatSpread"
                type="number"
                step="0.01"
                value={formData.floatSpread}
                onChange={(e) => handleInputChange('floatSpread', e.target.value)}
                placeholder="e.g., 0.00"
                disabled={isFieldDisabled('floatSpread')}
              />
            </FormGroup>
          </LegFormRow>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="floatPayFrequency">Pay Frequency</Label>
              <Select
                id="floatPayFrequency"
                value={formData.floatPayFrequency}
                onChange={(e) => handleInputChange('floatPayFrequency', e.target.value)}
                disabled={isFieldDisabled('floatPayFrequency')}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
                <option value="Annual">Annual</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="floatResetFrequency">Reset Frequency</Label>
              <Select
                id="floatResetFrequency"
                value={formData.floatResetFrequency}
                onChange={(e) => handleInputChange('floatResetFrequency', e.target.value)}
                disabled={isFieldDisabled('floatResetFrequency')}
              >
                <option value="Daily">Daily</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annual">Semi-Annual</option>
              </Select>
            </FormGroup>
          </LegFormRow>

          <LegFormRow>
            <FormGroup>
              <Label htmlFor="floatDayCount">Day Count</Label>
              <Select
                id="floatDayCount"
                value={formData.floatDayCount}
                onChange={(e) => handleInputChange('floatDayCount', e.target.value)}
                disabled={isFieldDisabled('floatDayCount')}
              >
                <option value="ACT/360">ACT/360</option>
                <option value="ACT/365">ACT/365</option>
                <option value="ACT/ACT">ACT/ACT</option>
                <option value="30/360">30/360</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="floatLeverage">Leverage</Label>
              <Input
                id="floatLeverage"
                type="number"
                step="0.1"
                value={formData.floatLeverage}
                onChange={(e) => handleInputChange('floatLeverage', e.target.value)}
                placeholder="1"
                disabled={isFieldDisabled('floatLeverage')}
              />
            </FormGroup>
          </LegFormRow>
        </SwapLegPanel>
      </SwapLegsContainer>
    </>
  );
};

export default DerivativesSwaptionTicketScreen;
